import React, { useState, useRef } from 'react';
import { X, Upload, FileText, CheckCircle, AlertCircle, Plus, Minus } from 'lucide-react';
import documentStorageService from '../../services/documentStorageService';

const ImprovedUploadModal = ({ 
  isOpen, 
  onClose, 
  onUploadComplete, 
  onError 
}) => {
  const [currentStep, setCurrentStep] = useState(1); // 1: Upload, 2: Metadata, 3: Review
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [globalMetadata, setGlobalMetadata] = useState({
    entityType: 'general',
    category: 'documents',
    issuingAuthority: '',
    notes: ''
  });

  const [fileMetadata, setFileMetadata] = useState({});

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    handleFileSelection(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleFileSelection(files);
  };

  const handleFileSelection = (files) => {
    const validFiles = files.filter(file => {
      const isValidType = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ].includes(file.type);
      
      const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB
      
      return isValidType && isValidSize;
    });

    if (validFiles.length !== files.length) {
      onError('Some files were skipped due to invalid type or size (max 50MB)');
    }

    setSelectedFiles(validFiles);
    
    // Initialize metadata for each file
    const metadata = {};
    validFiles.forEach(file => {
      metadata[file.name] = {
        title: file.name.split('.')[0],
        type: 'general',
        entityName: '',
        expirationDate: '',
        issueDate: '',
        documentNumber: ''
      };
    });
    setFileMetadata(metadata);

    if (validFiles.length > 0) {
      setCurrentStep(2);
    }
  };

  const removeFile = (fileName) => {
    setSelectedFiles(prev => prev.filter(file => file.name !== fileName));
    setFileMetadata(prev => {
      const newMetadata = { ...prev };
      delete newMetadata[fileName];
      return newMetadata;
    });
    
    if (selectedFiles.length === 1) {
      setCurrentStep(1);
    }
  };

  const updateFileMetadata = (fileName, field, value) => {
    setFileMetadata(prev => ({
      ...prev,
      [fileName]: {
        ...prev[fileName],
        [field]: value
      }
    }));
  };

  const handleUpload = async () => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const metadata = {
          ...globalMetadata,
          ...fileMetadata[file.name],
          uploadedBy: 'Current User',
          tags: ['upload', globalMetadata.category],
          issueDate: fileMetadata[file.name].issueDate || new Date().toISOString()
        };

        return await documentStorageService.uploadDocument(
          file,
          metadata,
          {
            enableOCR: documentStorageService.canOCRFile(file),
            onProgress: (progress) => {
              const totalProgress = ((index * 100) + progress) / selectedFiles.length;
              setUploadProgress(Math.round(totalProgress));
            }
          }
        );
      });

      const results = await Promise.all(uploadPromises);
      const successful = results.filter(result => result && result.success !== false);
      
      if (successful.length > 0) {
        onUploadComplete(successful);
        handleClose();
      } else {
        onError('All uploads failed');
      }
    } catch (error) {
      onError('Upload failed: ' + error.message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setSelectedFiles([]);
    setFileMetadata({});
    setGlobalMetadata({
      entityType: 'general',
      category: 'documents',
      issuingAuthority: '',
      notes: ''
    });
    setUploadProgress(0);
    setIsUploading(false);
    onClose();
  };

  const canProceedToReview = () => {
    return selectedFiles.every(file => {
      const meta = fileMetadata[file.name];
      return meta.title && meta.type;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Upload Documents</h3>
                <p className="text-sm text-gray-500">Add new documents to your library</p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Progress Steps */}
            <div className="mt-4">
              <div className="flex items-center">
                {[
                  { step: 1, label: 'Upload Files' },
                  { step: 2, label: 'Add Details' },
                  { step: 3, label: 'Review & Submit' }
                ].map((item, index) => (
                  <React.Fragment key={item.step}>
                    <div className="flex items-center">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                        currentStep >= item.step 
                          ? 'border-blue-600 bg-blue-600 text-white' 
                          : 'border-gray-300 bg-white text-gray-300'
                      }`}>
                        {currentStep > item.step ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <span className="text-sm font-medium">{item.step}</span>
                        )}
                      </div>
                      <span className={`ml-2 text-sm font-medium ${
                        currentStep >= item.step ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {item.label}
                      </span>
                    </div>
                    {index < 2 && (
                      <div className={`flex-1 h-0.5 mx-4 ${
                        currentStep > item.step ? 'bg-blue-600' : 'bg-gray-300'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-6 py-6">
            {currentStep === 1 && (
              <div className="space-y-6">
                {/* File Upload Area */}
                <div
                  className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive 
                      ? 'border-blue-400 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={handleFileInput}
                    className="hidden"
                  />
                  
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="text-blue-600 hover:text-blue-500 font-medium"
                    >
                      Choose files to upload
                    </button>
                    <p className="text-gray-500 text-sm mt-1">
                      or drag and drop files here
                    </p>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    PDF, JPG, PNG, DOC files up to 50MB each
                  </p>
                </div>

                {/* Selected Files Preview */}
                {selectedFiles.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">
                      Selected Files ({selectedFiles.length})
                    </h4>
                    <div className="space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <FileText className="h-8 w-8 text-blue-600 mr-3" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{file.name}</p>
                              <p className="text-xs text-gray-500">
                                {documentStorageService.formatFileSize ? 
                                  documentStorageService.formatFileSize(file.size) : 
                                  `${Math.round(file.size / 1024)} KB`
                                }
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => removeFile(file.name)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                {/* Global Settings */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Global Settings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Entity Type
                      </label>
                      <select
                        value={globalMetadata.entityType}
                        onChange={(e) => setGlobalMetadata(prev => ({ ...prev, entityType: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="general">General</option>
                        <option value="company">Company</option>
                        <option value="driver">Driver</option>
                        <option value="vehicle">Vehicle</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <select
                        value={globalMetadata.category}
                        onChange={(e) => setGlobalMetadata(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="documents">General Documents</option>
                        <option value="compliance">Compliance</option>
                        <option value="financial">Financial</option>
                        <option value="operational">Operational</option>
                        <option value="hr">Human Resources</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="safety">Safety</option>
                      </select>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Issuing Authority
                      </label>
                      <input
                        type="text"
                        value={globalMetadata.issuingAuthority}
                        onChange={(e) => setGlobalMetadata(prev => ({ ...prev, issuingAuthority: e.target.value }))}
                        placeholder="e.g., State DMV, DOT, Company"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Individual File Settings */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Document Details</h4>
                  <div className="space-y-6">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center mb-4">
                          <FileText className="h-5 w-5 text-blue-600 mr-2" />
                          <h5 className="font-medium text-gray-900">{file.name}</h5>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Document Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={fileMetadata[file.name]?.title || ''}
                              onChange={(e) => updateFileMetadata(file.name, 'title', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Document Type <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={fileMetadata[file.name]?.type || ''}
                              onChange={(e) => updateFileMetadata(file.name, 'type', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="">Select Type</option>
                              <option value="license">License</option>
                              <option value="medical">Medical Certificate</option>
                              <option value="insurance">Insurance</option>
                              <option value="registration">Registration</option>
                              <option value="inspection">Inspection</option>
                              <option value="certification">Certification</option>
                              <option value="training">Training Certificate</option>
                              <option value="permit">Permit</option>
                              <option value="maintenance">Maintenance Record</option>
                              <option value="contract">Contract</option>
                              <option value="invoice">Invoice</option>
                              <option value="receipt">Receipt</option>
                              <option value="report">Report</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Entity Name
                            </label>
                            <input
                              type="text"
                              value={fileMetadata[file.name]?.entityName || ''}
                              onChange={(e) => updateFileMetadata(file.name, 'entityName', e.target.value)}
                              placeholder="e.g., John Smith, Truck TR-001"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Document Number
                            </label>
                            <input
                              type="text"
                              value={fileMetadata[file.name]?.documentNumber || ''}
                              onChange={(e) => updateFileMetadata(file.name, 'documentNumber', e.target.value)}
                              placeholder="e.g., INV-2024-001"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Issue Date
                            </label>
                            <input
                              type="date"
                              value={fileMetadata[file.name]?.issueDate || ''}
                              onChange={(e) => updateFileMetadata(file.name, 'issueDate', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Expiration Date
                            </label>
                            <input
                              type="date"
                              value={fileMetadata[file.name]?.expirationDate || ''}
                              onChange={(e) => updateFileMetadata(file.name, 'expirationDate', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Review & Submit</h4>
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <h5 className="font-medium text-gray-900 mb-2">Upload Summary</h5>
                    <p className="text-sm text-gray-600">
                      {selectedFiles.length} document{selectedFiles.length !== 1 ? 's' : ''} ready for upload
                    </p>
                    <p className="text-sm text-gray-600">
                      Total size: {selectedFiles.reduce((total, file) => total + file.size, 0) > 1024 * 1024 
                        ? `${Math.round(selectedFiles.reduce((total, file) => total + file.size, 0) / (1024 * 1024))} MB`
                        : `${Math.round(selectedFiles.reduce((total, file) => total + file.size, 0) / 1024)} KB`
                      }
                    </p>
                  </div>

                  <div className="space-y-3">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-white border border-gray-200 rounded-lg">
                        <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{fileMetadata[file.name]?.title}</p>
                          <p className="text-sm text-gray-500">{file.name}</p>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className="text-xs text-gray-500">
                              Type: {fileMetadata[file.name]?.type?.replace('_', ' ') || 'Not set'}
                            </span>
                            {fileMetadata[file.name]?.entityName && (
                              <span className="text-xs text-gray-500">
                                Entity: {fileMetadata[file.name].entityName}
                              </span>
                            )}
                            {fileMetadata[file.name]?.expirationDate && (
                              <span className="text-xs text-gray-500">
                                Expires: {new Date(fileMetadata[file.name].expirationDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Upload Progress */}
                {isUploading && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <Upload className="h-5 w-5 text-blue-600 mr-2" />
                      <span className="text-sm font-medium text-blue-900">
                        Uploading documents...
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-blue-700 mt-1">{uploadProgress}% complete</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-between">
            <div>
              {currentStep > 1 && (
                <button
                  onClick={() => setCurrentStep(prev => prev - 1)}
                  disabled={isUploading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  Back
                </button>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleClose}
                disabled={isUploading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              
              {currentStep === 1 && selectedFiles.length > 0 && (
                <button
                  onClick={() => setCurrentStep(2)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Next: Add Details
                </button>
              )}
              
              {currentStep === 2 && (
                <button
                  onClick={() => setCurrentStep(3)}
                  disabled={!canProceedToReview()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next: Review
                </button>
              )}
              
              {currentStep === 3 && (
                <button
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {isUploading ? 'Uploading...' : 'Upload Documents'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImprovedUploadModal;